import type { PrimitiveValue } from "../common/components/computation-types";
import type { AppFacilities } from "../common/components/types";
import type { GroundValue, LoadedGroundValue, LoadedRow, LoadingValue, Row } from "../common/computation-model/data";
import type { RelativePath, RootPath } from "../common/computation-model/paths";
import type { TableName } from "../common/database-strings";
import type {
    ArrayTransform,
    ColumnType,
    FilterArrayTransform,
    Formula,
    InputOutputTables,
    LegacyPropertyDescription,
    MutatingScreenKind,
    SourceColumn,
    TableGlideType,
} from "../common/description";
import type { Sound } from "../common/sound";
import type { ExistingAppDescriptionContext } from "../component-utils";
import type { WireAction, WireComponent, WireEditableValue } from "../wire/types";
import type { WireScreen } from "./nav-model";
import type { PageScreenPosition, ParsedPath, ParsedScreen } from "./parsed-path";

export enum PageScreenTarget {
    Current = "current",
    Main = "main",
    XSmallModal = "x-small-modal",
    SmallModal = "small-modal",
    LargeModal = "large-modal",
}

// These must all easily be implementable over a wire protocol.  In particular
// they can't return values, and they're not expected to execute
// synchronously.
export interface WireActionCallbacks {
    openLink(url: string): void;
    showToast(success: boolean, message: string): void;
    copyToClipboard(data: string): void;
    playSound(sound: Sound): void;
    signOut(): void;
}

export interface WireValueChangedBackend {
    valueChanged(token: string, value: PrimitiveValue): void;
}

export interface WireActionBackend extends WireValueChangedBackend {
    readonly actionCallbacks: WireActionCallbacks;
    readonly timesOutAt: Date;

    makeBackendForSubAction(): WireRowActionHydrationValueProvider;

    pushFreeScreen(screenName: string, target: PageScreenTarget, row: Row | undefined): void;
    pushDefaultClassScreen(tableName: TableName, row: Row, target: PageScreenTarget): void;
    pushDefaultEditScreen(tableName: TableName, row: Row, target: PageScreenTarget): void;
    pushFormScreen(screenName: string, row: Row | undefined, target: PageScreenTarget): void;

    closeScreen(position: PageScreenPosition.Modal): void;

    navigateToTab(tabScreenName: string): boolean;
    navigateToPath(path: ParsedPath): boolean;
    navigateToUserProfile(): boolean;
    navigateUp(): boolean;

    addRow(tableName: TableName, row: LoadedRow): Promise<boolean>;
    setColumnsInRow(tableName: TableName, row: Row, updates: Record<string, LoadedGroundValue>): Promise<boolean>;
    deleteRow(tableName: TableName, row: Row): Promise<boolean>;

    saveEditScreenChanges(tableName: TableName, screenContext: InternalScreenContext): Promise<boolean>;

    signIn(type: "sign-in" | "sign-up"): void;
}

export interface InternalScreenContext {
    // This is always a visible row
    readonly inputRow: Row | undefined;
    // This row, if present, is always invisible, and has to be deleted from
    // the table when the screen is popped.  All edit screens will have this
    // set. For regular detail screens this is not set, and the input row also
    // acts as the output row.  Unfortunately this is wrong because it makes
    // us guess whether we have the ##correctOutputRow if we have to fall back
    // to the input row.
    readonly outputRow: Row | undefined;
}

export interface InternalRowContext extends InternalScreenContext {
    // This is always a visible row
    readonly containingScreenRow: Row | undefined;
}

export type WireActionHydrationResult =
    | WireActionRunner
    | [runner: WireActionRunner, url: string | undefined]
    | LoadingValue
    | undefined;
export type WireActionHydrator = (
    hb: WireRowActionHydrationValueProvider,
    treatLoadingAsUndefined: boolean
) => WireActionHydrationResult;

export type WireActionRunner = (b: WireActionBackend, handledByFrontend: boolean) => Promise<boolean>;

export interface WireComponentWithFlags {
    readonly component: WireComponent;
    readonly isValid: boolean;
    readonly hasValue: boolean;
}

export type WireHydrationFollowUp = (ab: WireActionBackend) => void;

export interface WireComponentHydrationResult extends WireComponentWithFlags {
    readonly followUp?: WireHydrationFollowUp;
}

export type WireRowComponentHydrator = (
    hb: WireRowComponentHydrationBackend
) => WireComponentHydrationResult | undefined;
// For array screens the `rhb` will be `undefined`
export type WireTableComponentHydrator = (
    thb: WireTableComponentHydrationBackend,
    rhb: WireRowComponentHydrationBackend | undefined
) => WireComponentHydrationResult | undefined;
export type WireScreenHydrator = (
    defaultTitle: string | undefined,
    searchBarValue: string,
    backButtonAction: WireAction | undefined,
    searchBarOnChangeToken: string,
    hb: WireTableComponentHydrationBackend
) => WireScreen | undefined;

export type WireValueGetter = (hb: WireRowHydrationValueProvider) => GroundValue | null;
export type WireRowComponentValueGetter = (hb: WireRowComponentHydrationBackend) => GroundValue | null;
export type WirePredicate = (hb: WireRowHydrationValueProvider) => boolean | LoadingValue;

export interface ContextTableTypes extends InputOutputTables {
    readonly containingScreen: TableGlideType | undefined;
}

export interface WireValueInflationBackend {
    readonly adc: ExistingAppDescriptionContext;
    readonly appFacilities: AppFacilities;
    readonly tables: ContextTableTypes;
    readonly mutatingScreenKind: MutatingScreenKind | undefined;
    // We only hydrate component IDs when hydrating for the builder.
    readonly forBuilder: boolean;

    getValueGetterForColumn(
        sourceColumn: SourceColumn,
        inOutputRow: boolean,
        withFormat: boolean
    ): readonly [WireValueGetter, ColumnType];
    getValueGetterForProperty(
        desc: LegacyPropertyDescription | undefined,
        withFormat: boolean,
        opts?: {
            inOutputRow?: boolean; // default: false
            columnFirst?: boolean; // default: true
        }
    ): readonly [WireValueGetter, ColumnType];

    inflatePredicate(predicate: Formula, inOutputRow: boolean): WirePredicate | undefined;
    inflateFilters(
        filters: readonly FilterArrayTransform[],
        inOutputRow: boolean,
        tableTypeOverride: TableGlideType | undefined
    ): WirePredicate;
}

export interface WireInflationBackend extends WireValueInflationBackend {
    makeInflationBackendForTables(tables: ContextTableTypes): WireRowComponentInflationBackend;
}

export type WireTableGetter = (
    hb: WireRowComponentHydrationBackend
) => WireTableComponentHydrationBackend | LoadingValue | undefined;

export interface WireRowComponentInflationBackend extends WireInflationBackend {
    getValueGetterForTableComponent(
        desc: LegacyPropertyDescription,
        transforms: readonly ArrayTransform[]
    ): [WireTableGetter, WireInflationBackend] | undefined;
}

export interface WireHydrationBackend {
    registerAction(run: WireActionRunner): string;
    getParsedPath(): ParsedPath;
}

interface WireHydrationValueProvider {}

export interface WireRowHydrationValueProvider extends WireHydrationValueProvider {
    // FIXME: This need to be behind accessors, too, to enable subscribing.
    // Even checking against `undefined` must include some kind of
    // subscription.
    readonly rowContext: InternalRowContext | undefined;

    // FIXME: These are just used internally in the backend and shouldn't be
    // here, probably.
    getGlobalValue(tableName: TableName | undefined, rootPath: RootPath): GroundValue;
    getColumnInRow(tableName: TableName, rootPath: RootPath, row: Row, columnPath: RelativePath): GroundValue;
}

export interface WireRowActionHydrationValueProvider extends WireRowHydrationValueProvider {
    getIsUserSignedIn(): boolean;
    getIsTabVisible(tabScreenName: string): boolean;
    getIsModalVisible(): boolean;

    fetchTable(tableName: TableName): void;

    makeHydrationBackendForRow(
        row: Row | undefined,
        tables: InputOutputTables | undefined
    ): WireRowActionHydrationValueProvider;
}

export interface WireRowValueProviderMaker {
    makeRowValueProvider(row: Row): WireRowHydrationValueProvider;
}

export interface WireTableTransformValueProvider extends WireRowValueProviderMaker {
    makeHydrationBackendForTable(rows: Row[]): WireTableComponentHydrationBackend;
}

export interface WireAlwaysEditableValue<T> extends WireEditableValue<T> {
    readonly onChangeToken: string;
}

export interface WireHydratedSubComponents {
    readonly components: readonly (WireComponent | null)[];
    readonly allValid: boolean;
    readonly haveValues: boolean;
    readonly componentsRowContext: InternalRowContext | undefined;
}

export interface WireRowComponentHydrationBackend extends WireHydrationBackend, WireRowActionHydrationValueProvider {
    readonly screenPosition: PageScreenPosition;
    readonly currentScreenTitle: string | undefined;

    // FIXME: This should take a table, because we can change values outside
    // the context row, but right now that's not supported.
    registerOnValueChange(columnName: string): string | undefined;

    makeTableTransformValueProvider(tableType: TableGlideType): WireTableTransformValueProvider | undefined;

    // `shouldSave` defaults to `true`.  `convert`, if present, converts the
    // value coming in from the frontend to whatever the backend actually
    // wants to store.
    getState<T>(
        name: string,
        validate: (v: unknown) => v is T,
        defaultValue: T,
        shouldSave?: boolean,
        convert?: (v: unknown) => T
    ): WireAlwaysEditableValue<T>;

    makeHydrationBackendForRow(
        row: Row | undefined,
        tables: InputOutputTables | undefined
    ): WireRowComponentHydrationBackend;
    makeHydrationBackendForOutputRow(): WireRowComponentHydrationBackend | undefined;

    getScreenTitle(screen: ParsedScreen): string | null | undefined;

    // May only be called at most once per component.  If `outputTable` is
    // defined, sub-components will be hydrated with an invisible output row
    // from that table.  We use that for form containers.
    hydrateSubComponents(
        hydrators: readonly WireComponentHydratorWithID[],
        outputTable: TableGlideType | undefined
    ): WireHydratedSubComponents;
}

export interface WireTableComponentHydrationBackend extends WireHydrationBackend, WireHydrationValueProvider {
    // FIXME: This need to be behind accessors, too, to enable subscribing.
    // Even checking against `undefined` must include some kind of
    // subscription.
    readonly tableScreenContext: readonly Row[];

    makeHydrationBackendForRow(row: Row): WireRowComponentHydrationBackend;

    withRows(rows: readonly Row[]): WireTableComponentHydrationBackend;
}

export type WireComponentHydratorWithID = [hydrator: WireRowComponentHydrator, componentID: string | undefined];
